/* eslint-disable import/no-cycle */
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EnvironmentInjector,
  OnInit,
  computed,
  createEnvironmentInjector,
  inject,
  input,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute } from '@angular/router'

import { distinctUntilChanged, map, skip } from 'rxjs'

import { FlxStyleModule, FlxThemeModule } from '@flowx/angular-theme'
import { FlxTabBarModule, FlxTabComponent } from '@flowx/angular-ui-toolkit'
import {
  ComponentIdentifierEnum,
  StorageManagementService,
  TabBarTemplateConfig,
  TabTemplateConfig,
  getUrlQueryParamValue,
  isUserTaskRoot,
} from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxTreeNodeComponent } from '../../renderer/tree-node/tree-node.component'
import { FlxActionService } from '../../services/flx-action.service'
import { emitScreenAnalyticsOnDisplay } from '../../utils/screen-analytics.utils'
import { getContentIcon, getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-tab-bar-wrapper',
  imports: [FlxTabBarModule, FlxTreeNodeComponent, FlxLocalizePipe, FlxThemeModule, FlxStyleModule],
  templateUrl: './tab-bar-wrapper.component.html',
  styleUrl: './tab-bar-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxTabBarWrapperComponent implements OnInit {
  #actionService = inject(FlxActionService)
  #activatedRoute = inject(ActivatedRoute)
  #destroyRef = inject(DestroyRef)
  #envInjector = inject(EnvironmentInjector)
  #tabEventInjector?: EnvironmentInjector

  templateConfig = input.required<TabBarTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  selectedTabIndex = signal(0)

  tabs = computed(() => {
    const tabs = this.templateConfig().templateConfig.filter(
      (tc) => tc.componentIdentifier === ComponentIdentifierEnum.TAB
    ) as TabTemplateConfig[]
    return tabs.map((tab) => ({
      ...tab,
      title: getDynamicPropSignal(tab.displayOptions.flowxProps.title, this.templateConfig()),
      ariaLabel: tab.displayOptions.flowxProps.accessibility?.label,
    }))
  })

  ngOnInit(): void {
    const config = this.templateConfig()

    // Trigger onLoad for root tab bars, with optional load workflow
    this.#actionService.runOnLoadEvent(config, this.#destroyRef, isUserTaskRoot)

    // Trigger onDisplay for root tab bars
    this.#actionService.runOnDisplayEvent(config, this.#destroyRef, isUserTaskRoot)

    // Navigation areas are screen roots in UI flows and emit the same SCREEN event a user
    // task root container does. Armed after onLoad so it waits on that load, not past it.
    emitScreenAnalyticsOnDisplay(config, this.flowxProps(), this.#destroyRef)

    const foundIndex = this.#findSelectedTabIndex()
    const initialIndex = foundIndex > -1 ? foundIndex : 0
    this.selectedTabIndex.set(initialIndex)
    this.#dispatchSelectedTabEvents(initialIndex)
    this.#destroyRef.onDestroy(() => this.#tabEventInjector?.destroy())

    // special tab bar handling for ui flows -> listen to viewId query changes and use it to find the selected tab
    this.#activatedRoute.queryParams
      .pipe(
        map((params) => params.viewId),
        skip(1),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        const index = this.#findSelectedTabIndex()
        this.selectedTabIndex.set(index)
        this.#dispatchSelectedTabEvents(index)
      })
  }

  getIcon(icon: { value: string; appId: string }): string | null {
    return getContentIcon(icon)
  }

  setSelectedTab(tab: FlxTabComponent): void {
    const foundIndex = this.tabs().findIndex((t) => t.id === tab.id())
    const index = foundIndex > -1 ? foundIndex : 0
    this.selectedTabIndex.set(index)
    this.#dispatchSelectedTabEvents(index)
    this.#persistSelectedTabInStorage(tab)
  }

  #dispatchSelectedTabEvents(index: number): void {
    const selectedTab = this.tabs()[index]
    if (!selectedTab) {
      return
    }
    // Tear down the previous selection's trigger subscriptions before re-arming.
    this.#tabEventInjector?.destroy()
    this.#tabEventInjector = createEnvironmentInjector([], this.#envInjector)
    const destroyRef = this.#tabEventInjector.get(DestroyRef)
    this.#actionService.runOnLoadEvent(selectedTab, destroyRef, () => true)
    this.#actionService.runOnDisplayEvent(selectedTab, destroyRef, () => true)
  }

  #persistSelectedTabInStorage(selectedTab: any): void {
    const { templateConfig, id, processInstanceUuid } = this.templateConfig()
    if (!processInstanceUuid) {
      return
    }
    const selectedTabTc = templateConfig.find((t) => t.id === selectedTab.id())

    let existingSelectedTabs =
      StorageManagementService.getData('localStorage', 'selectedTabs') || []

    const tabBarHasSelectedTab = existingSelectedTabs.find((tab) => tab.parentId === id)

    if (tabBarHasSelectedTab) {
      existingSelectedTabs = [
        ...existingSelectedTabs.filter((tab) => tab.parentId !== id),
        {
          processUuid: processInstanceUuid,
          parentId: id,
          id: selectedTabTc.id,
          context: selectedTabTc.context,
        },
      ]
    } else {
      existingSelectedTabs = [
        ...existingSelectedTabs,
        {
          processUuid: processInstanceUuid,
          parentId: id,
          id: selectedTabTc.id,
          context: selectedTabTc.context,
        },
      ]
    }

    StorageManagementService.setData('localStorage', existingSelectedTabs, 'selectedTabs')
  }

  #findSelectedTabIndex(): number {
    // special tab bar handling for ui flows -> if viewId is present, use it to find the selected tab -> either the tab or a first descendant
    const viewId = getUrlQueryParamValue('viewId')
    if (viewId) {
      return (
        this.tabs().findIndex(
          (tab) =>
            tab.flowxUuid === viewId ||
            tab.templateConfig.map((tc) => tc.flowxUuid).includes(viewId)
        ) ?? 0
      )
    }

    const { id } = this.templateConfig()
    const savedSelectedTabs = StorageManagementService.getData('localStorage', 'selectedTabs') || []
    const foundTabInLocalStorage = savedSelectedTabs.find(
      (tab) =>
        tab.parentId === id &&
        // tab.processUuid === processInstanceUuid && <- coment this back in when we resume processes (right now we start a new process on each reload)
        this.tabs().find((t) => t.id === tab.id)
    )
    return this.tabs().findIndex((tab) => tab.id === foundTabInLocalStorage?.id) || 0
  }
}
